import { supabase } from '@/config/supabase';

/**
 * Get public URL for person profile image
 * Returns temporary signed URL (valid for 1 hour)
 *
 * @param imagePath - Storage path from profile_image_url column
 * @returns Promise<string | null>
 */
export async function getPersonImageUrl(imagePath: string | null): Promise<string | null> {
  if (!imagePath) return null;

  try {
    const { data, error } = await supabase.storage
      .from('person-images')
      .createSignedUrl(imagePath, 3600);  // 1 hour expiry

    if (error) {
      console.error('[Storage API] Failed to generate signed URL:', error);
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    console.error('[Storage API] Image URL generation error:', error);
    return null;
  }
}

/**
 * Batch generate signed URLs for multiple images
 * More efficient than individual requests
 *
 * @param imagePaths - Array of storage paths
 * @returns Promise<Map<string, string>>
 */
export async function batchGetImageUrls(imagePaths: string[]): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();

  // Filter out nulls and empty strings
  const validPaths = imagePaths.filter(p => p !== null && p !== '');

  if (validPaths.length === 0) return urlMap;

  try {
    console.log(`[Storage API] Batch generating ${validPaths.length} signed URLs...`);

    // Batch request (Supabase supports up to 100 at once)
    const signedUrlPromises = validPaths.map(path =>
      supabase.storage
        .from('person-images')
        .createSignedUrl(path, 3600)
        .then(({ data, error }) => {
          if (!error && data) {
            urlMap.set(path, data.signedUrl);
          } else if (error) {
            console.warn(`[Storage API] Failed to generate URL for ${path}:`, error);
          }
        })
    );

    await Promise.all(signedUrlPromises);

    console.log(`[Storage API] Successfully generated ${urlMap.size} signed URLs`);
  } catch (error) {
    console.error('[Storage API] Batch image URL generation error:', error);
  }

  return urlMap;
}

/**
 * Helper: Get avatar fallback emoji based on gender
 */
export function getAvatarFallback(gender: 'male' | 'female' | 'other'): string {
  const fallbacks = {
    male: '👨',
    female: '👩',
    other: '🧑',
  };

  return fallbacks[gender] || fallbacks.other;
}

/**
 * Helper: Get avatar color classes based on gender
 */
export function getAvatarColor(gender: 'male' | 'female' | 'other'): string {
  const colors = {
    male: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    female: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    other: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  };

  return colors[gender] || colors.other;
}

/**
 * Upload person profile image to storage
 * Generates unique filename and uploads to person-images bucket
 *
 * @param personId - ID of the person (for path organization)
 * @param file - Image file to upload
 * @returns Promise<string> - Storage path of uploaded image
 */
export async function uploadPersonImage(personId: string, file: File): Promise<string> {
  try {
    console.log('[Storage API] Uploading image for person:', personId);

    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${personId}-${Date.now()}.${fileExt}`;
    const filePath = `profiles/${fileName}`;

    // Upload file
    const { error: uploadError } = await supabase.storage
      .from('person-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      console.error('[Storage API] Upload error:', uploadError);
      throw new Error('Failed to upload image');
    }

    console.log('[Storage API] Image uploaded successfully:', filePath);
    return filePath;
  } catch (error) {
    console.error('[Storage API] Image upload error:', error);
    throw error;
  }
}

/**
 * Delete person profile image from storage
 *
 * @param imagePath - Storage path to delete
 * @returns Promise<void>
 */
export async function deletePersonImage(imagePath: string): Promise<void> {
  try {
    console.log('[Storage API] Deleting image:', imagePath);

    const { error } = await supabase.storage
      .from('person-images')
      .remove([imagePath]);

    if (error) {
      console.error('[Storage API] Delete error:', error);
      throw new Error('Failed to delete image');
    }

    console.log('[Storage API] Image deleted successfully');
  } catch (error) {
    console.error('[Storage API] Image deletion error:', error);
    throw error;
  }
}
